import { useEffect, useMemo, useRef, useState } from 'react';

const PAGE_KEY = 'sahal_page_';
const SIZE_KEY = 'sahal_page_size_';

const readNumber = (key, fallback) => {
  try {
    const raw = sessionStorage.getItem(key);
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  } catch {
    return fallback;
  }
};

const writeNumber = (key, value) => {
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    // ignore
  }
};

const usePagination = (items = [], { pageSize: defaultSize = 10, resetKey = '', persistKey } = {}) => {
  const [pageSize, setPageSize] = useState(() =>
    persistKey ? readNumber(SIZE_KEY + persistKey, defaultSize) : defaultSize
  );
  const [page, setPage] = useState(() =>
    persistKey ? readNumber(PAGE_KEY + persistKey, 1) : 1
  );

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const skipReset = useRef(true);

  useEffect(() => {
    if (skipReset.current) {
      skipReset.current = false;
      return;
    }
    setPage(1);
  }, [resetKey, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (!persistKey) return;
    writeNumber(PAGE_KEY + persistKey, page);
    writeNumber(SIZE_KEY + persistKey, pageSize);
  }, [page, pageSize, persistKey]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    total,
    totalPages,
    pagedItems
  };
};

export default usePagination;
